import React from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';
import classNameBind from 'classnames/bind';
import moment from 'moment';
import { getMonths, getWeekDays } from '../../../../formatters';
import Button from '../../../Button';
import defaultClassNames from './defaultClassNames';
import styles from './styles.module.css';
import { CUSTOM, DAY, MONTH, WEEK, QUARTER } from '../../timePeriods';
import getDateInAllowedRange from '../../methods/getDateInAllowedRange';
import getDate from '../../methods/getDate';
import CaptionElement from '../CaptionElement';
import 'react-day-picker/lib/style.css';

const bindClasses = classNameBind.bind(styles);

const classNames = Object.entries(defaultClassNames).reduce(
  (accumulator, [label, classes]) => ({ ...accumulator, [label]: bindClasses(classes.split(' ')) }),
  {},
);

const disabledClass = classNames.disabled;

class CalendarRangePickerNew extends React.Component {
  static isDayEnabled(modifiers) {
    return !modifiers[disabledClass];
  }

  periodMomentNames = {
    [DAY]: 'day',
    [MONTH]: 'month',
    [WEEK]: 'isoWeek',
    [QUARTER]: 'quarter',
  };

  constructor(props) {
    super(props);
    this.state = {
      selectionStart: null,
      selectionEnd: null,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const {
      from,
      to,
      min,
      max,
    } = props;

    let secondCalendarMonth = getDate(getDateInAllowedRange(to, min, max)) || new Date();
    let firstCalendarMonth = getDate(
      getDateInAllowedRange(from, min, max) || moment(secondCalendarMonth).subtract(1, 'month'),
    );

    const {
      firstCalendarMonth: previousFirstCalendarMonth,
      secondCalendarMonth: previousSecondCalendarMonth,
      oldFirstCalendarMonth,
      oldSecondCalendarMonth,
    } = state;

    if (previousFirstCalendarMonth && previousSecondCalendarMonth) {
      if (DateUtils.isSameMonth(firstCalendarMonth, secondCalendarMonth)) {
        if (previousFirstCalendarMonth !== oldFirstCalendarMonth && typeof oldFirstCalendarMonth !== 'undefined') {
          firstCalendarMonth = previousFirstCalendarMonth;
        }
        if (previousSecondCalendarMonth !== oldSecondCalendarMonth && typeof oldSecondCalendarMonth !== 'undefined') {
          secondCalendarMonth = previousSecondCalendarMonth;
        }
        if (DateUtils.isSameMonth(firstCalendarMonth, previousFirstCalendarMonth)) {
          secondCalendarMonth = previousSecondCalendarMonth;
        } else {
          firstCalendarMonth = previousFirstCalendarMonth;
        }
      } else {
        if (previousFirstCalendarMonth !== oldFirstCalendarMonth && typeof oldFirstCalendarMonth !== 'undefined') {
          firstCalendarMonth = previousFirstCalendarMonth;
        }
        if (previousSecondCalendarMonth !== oldSecondCalendarMonth && typeof oldSecondCalendarMonth !== 'undefined') {
          secondCalendarMonth = previousSecondCalendarMonth;
        }
        if (DateUtils.isSameMonth(firstCalendarMonth, secondCalendarMonth)) {
          secondCalendarMonth = moment(secondCalendarMonth).add(1, 'month').toDate();
        }
      }
    } else if (DateUtils.isSameMonth(firstCalendarMonth, secondCalendarMonth)) {
      secondCalendarMonth = moment(secondCalendarMonth).add(1, 'month').toDate();
    }

    return {
      firstCalendarMonth,
      secondCalendarMonth,
    };
  }

  componentWillUnmount() {
    this.props.onClose();
  }

  /**
   * Produces period range based on date included in the period.
   *
   * @param {Date} date
   * @param {string} period Period enum (day, week, month).
   * @return {{selectionStart: Date, selectionEnd: Date}}
   */
  getPeriodRange = (date, period) => {
    const periodName = this.periodMomentNames[period];
    let selectionStart;
    let selectionEnd;

    if (periodName) {
      selectionStart = moment(date).startOf(periodName);
      selectionEnd = moment(selectionStart).endOf(periodName);
    }

    return {
      selectionStart: selectionStart ? selectionStart.toDate() : null,
      selectionEnd: selectionEnd ? selectionEnd.toDate() : null,
    };
  };

  /**
   * Produces valid selected range whenever selection was done chronologically or in reverse.
   *
   * @param {Date|null} endDate Forced selection end date.
   * @return {{from: Date, to: Date}}
   */
  getSelectedRange = (endDate = null) => {
    const {
      selectionStart,
      selectionEnd,
    } = this.state;

    const enforcedEnd = endDate || selectionEnd;

    if (selectionStart && enforcedEnd) {
      const from = new Date(Math.min(selectionStart, enforcedEnd));
      const to = new Date(Math.max(selectionStart, enforcedEnd));

      return {
        from,
        to,
      };
    }

    return {};
  };

  isSelectingNewRange = () => Boolean(this.state.selectionStart);

  handleDayClick = (day, modifiers) => {
    if (CalendarRangePickerNew.isDayEnabled(modifiers)) {
      const { period } = this.props;

      if (period === CUSTOM) {
        if (this.isSelectingNewRange()) {
          const { from, to } = this.getSelectedRange(day);

          this.setState({
            selectionStart: null,
            selectionEnd: null,
          });

          if (from && to) {
            this.handleChange(from, to, CUSTOM);
          }
        } else {
          this.setState({ selectionStart: day });
        }
      } else if ([DAY, WEEK, MONTH, QUARTER].indexOf(period) === -1) {
        this.props.forceChangeTimePeriod({ value: 'custom' });
        if (this.isSelectingNewRange()) {
          const { from, to } = this.getSelectedRange(day);

          this.setState({
            selectionStart: null,
            selectionEnd: null,
          });

          if (from && to) {
            this.handleChange(from, to, CUSTOM);
          }
        } else {
          this.setState({ selectionStart: day });
        }
      } else {
        const { selectionStart, selectionEnd } = this.getPeriodRange(day, period);
        this.handleChange(selectionStart, selectionEnd, period);
      }
    }
  };

  handleDayMouseEnter = (day, modifiers) => {
    const { period } = this.props;
    if (CalendarRangePickerNew.isDayEnabled(modifiers)) {
      if (period === CUSTOM) {
        if (this.isSelectingNewRange()) {
          this.setState({ selectionEnd: day });
        }
      } else {
        this.setState(this.getPeriodRange(day, period));
      }
    }
  };

  handleDayMouseLeave = () => {
    const { period } = this.props;
    if (period !== CUSTOM) {
      this.setState({
        selectionStart: null,
        selectionEnd: null,
      });
    }
  };

  handleChange = (from, to, period) => {
    this.props.onChange(moment(from), moment(to), period);
  };

  renderNavbar = ({
    onPreviousClick,
    onNextClick,
    showPreviousButton,
    showNextButton,
    className,
  }) => (
    <div className={bindClasses(className, 'rangepicker-navbarWrapper')} data-ppuc-id="ReportDateRangePicker.Navbar">
      <div className={bindClasses('prev')} data-ppuc-id="ReportDateRangePicker.Navbar.previous">
        <Button
          icon="arrow-left"
          disabled={!showPreviousButton}
          onClick={() => onPreviousClick()}
        />
      </div>
      <div className={bindClasses('next')} data-ppuc-id="ReportDateRangePicker.Navbar.next">
        <Button icon="arrow-right" disabled={!showNextButton} onClick={() => onNextClick()} />
      </div>
    </div>
  )

  render() {
    const {
      firstDayOfWeek,
      period,
      from: rawFrom,
      to: rawTo,
      min: rawMin,
      max: rawMax,
      language,
      comparisonEnabled,
      comparisonEnd,
      comparisonStart,
      selectionMode,
      comparisonMode,
    } = this.props;
    const min = getDate(rawMin);
    const max = getDate(rawMax);
    const from = getDate(getDateInAllowedRange(rawFrom, rawMin, rawMax));
    const to = getDate(getDateInAllowedRange(rawTo, rawMin, rawMax));
    const {
      firstCalendarMonth,
      secondCalendarMonth,
    } = this.state;
    const disabledDays = {
      after: max,
      before: min,
    };
    const firstCalendarToLimit = moment(secondCalendarMonth)
      .subtract(1, 'month')
      .toDate();
    const secondCalendarFromLimit = moment(firstCalendarMonth)
      .add(1, 'month')
      .toDate();

    const customSelection = period === CUSTOM && this.isSelectingNewRange() && ((comparisonEnabled && selectionMode === 'normal') || !comparisonEnabled);
    const customCompare = period === CUSTOM && (comparisonMode === 'custom_range' || comparisonMode === 'previous_time') && this.isSelectingNewRange() && ((comparisonEnabled && selectionMode === 'compare') || !comparisonEnabled);

    const selectedRange = this.getSelectedRange();
    const selectedDays = [{
      from: customSelection ? selectedRange.from : from,
      to: customSelection ? selectedRange.to : to,
    }, ...(comparisonEnabled ? [{
      // eslint-disable-next-line no-nested-ternary
      from: customCompare ? selectedRange.from : comparisonStart ? comparisonStart.toDate() : null,
      // eslint-disable-next-line no-nested-ternary
      to: customCompare ? selectedRange.to : comparisonEnd ? comparisonEnd.toDate() : null,
    }] : [])];

    const modifiers = {
      [bindClasses('DayPicker-Day--start', 'DayPicker-Day-initial--start')]: selectedDays[0].from,
      [bindClasses('DayPicker-Day--end', 'DayPicker-Day-initial--end')]: selectedDays[0].to,
      [bindClasses('DayPicker-Day-initial')]: selectedDays[0],
      [bindClasses('DayPicker-Day--hover', selectionMode)]: customSelection || customCompare ? null : selectedRange,
      ...(comparisonEnabled ? {
        [bindClasses('DayPicker-Day--compare-start')]: selectedDays[1].from,
        [bindClasses('DayPicker-Day--compare-end')]: selectedDays[1].to,
        [bindClasses('DayPicker-Day--compare')]: selectedDays[1],
      } : {}),
    };

    const renderDayPicker = (fromMonth, toMonth, onMonthChange, month, captionChange, captionMin, captionMax) => (
      <DayPicker
        classNames={classNames}
        selectedDays={selectedDays}
        disabledDays={disabledDays}
        modifiers={modifiers}
        firstDayOfWeek={firstDayOfWeek}
        showOutsideDays
        enableOutsideDaysClick={false}
        onDayClick={this.handleDayClick}
        onDayMouseEnter={this.handleDayMouseEnter}
        onDayMouseLeave={this.handleDayMouseLeave}
        months={getMonths(language, 'long')}
        weekdaysLong={getWeekDays(language, 'long')}
        weekdaysShort={getWeekDays(language, 'short')}
        fromMonth={fromMonth}
        navbarElement={this.renderNavbar}
        toMonth={toMonth}
        onMonthChange={onMonthChange}
        month={month}
        captionElement={date => (
          <CaptionElement
            date={date.date}
            onChange={captionChange}
            min={captionMin}
            max={captionMax}
            language={language}
          />
        )}
      />
    );

    return (
      <div className={bindClasses('wrapper-new-range', `DatePicker-Mode-Selection-${selectionMode}`)} data-ppuc-id="ReportDateRangePicker.CalendarRangePicker">
        {renderDayPicker(
          min,
          max,
          (month) => {
            this.setState({ firstCalendarMonth: month, oldFirstCalendarMonth: firstCalendarMonth });
          },
          firstCalendarMonth,
          (modifiedDate) => {
            this.setState({
              firstCalendarMonth: modifiedDate,
              oldFirstCalendarMonth: firstCalendarMonth,
            });
          },
          min,
          firstCalendarToLimit,
        )}
        {renderDayPicker(
          min,
          max,
          (month) => {
            this.setState({
              secondCalendarMonth: month,
              oldSecondCalendarMonth: secondCalendarMonth,
            });
          },
          secondCalendarMonth,
          (modifiedDate) => {
            this.setState({
              secondCalendarMonth: modifiedDate,
              oldSecondCalendarMonth: secondCalendarMonth,
            });
          },
          secondCalendarFromLimit,
          max,
        )}
      </div>
    );
  }
}

CalendarRangePickerNew.defaultProps = {
  from: null,
  to: null,
  comparisonStart: null,
  comparisonEnd: null,
  min: null,
  max: null,
  comparisonEnabled: false,
  onClose: () => {},
  selectionMode: '',
  comparisonMode: '',
  forceChangeTimePeriod: () => {},
};

export default CalendarRangePickerNew;
